import { gqlFetcher, graphql } from '#features/api';
import type { PresenceStatus } from './presenceStore';

const presenceDoc = graphql(`
  query Presence($userIds: [UUID!]!) {
    presence(userIds: $userIds) {
      userId
      status
    }
  }
`);

type PresenceQueryResult = {
  presence?: Array<{ userId?: string | null; status?: string | null } | null> | null;
};

/**
 * Шлюз отказывает, если спросить больше этого за раз. Раньше он молча срезал лишнее, и клиент не
 * отличал усечение от «не загрузилось»; теперь это ошибка запроса — поэтому режем список сами.
 * Значение должно совпадать с `PresenceApi.MAX_USER_IDS`.
 */
const MAX_USER_IDS_PER_REQUEST = 200;

const chunk = <T,>(items: T[], size: number): T[][] => {
  const chunks: T[][] = [];

  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }

  return chunks;
};

const fetchPresenceChunk = async (userIds: string[]) => {
  const data = (await gqlFetcher(presenceDoc, { userIds })) as PresenceQueryResult;

  return (data?.presence ?? []).flatMap((entry) =>
    entry?.userId && entry.status ? [{ userId: entry.userId, status: entry.status as PresenceStatus }] : []
  );
};

/**
 * Спрашиваем статусы пачками, а не одной: экран со списком людей легко перебирает потолок шлюза,
 * а терять часть списка нельзя — у пропавших статус остался бы неизвестным навсегда, и точка на
 * аватаре просто не появилась бы.
 */
export const fetchPresence = async (userIds: string[]) => {
  if (userIds.length === 0) {
    return [];
  }

  if (userIds.length <= MAX_USER_IDS_PER_REQUEST) {
    return fetchPresenceChunk(userIds);
  }

  const batches = await Promise.all(
    chunk(userIds, MAX_USER_IDS_PER_REQUEST).map((batch) => fetchPresenceChunk(batch))
  );

  return batches.flat();
};
